import React, { ReactNode, useState } from 'react';
import FormInput from './form-input.tsx';
import { GlobalUserDetails } from '../app.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export type ModalContent = { title: string; body: string };

interface ModalProps {
	trigger: (openModal: () => void) => ReactNode;
	userDetails: GlobalUserDetails;
	onSubmit: (userDetails: GlobalUserDetails, tscsSelected: boolean, optinSelected: boolean) => Promise<boolean>;
	updateUserDetails: (key: string, value: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export default function PdfModal(props: ModalProps) {

	// state.

	const [optinSelected, setOptinSelected] = useState(true);
	const [tscsSelected, setTscsSelected] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);

	// handlers.

	const openModal = () => {
		setIsOpen(true);
	}

	const closeModal = () => {
		setIsOpen(false);
	}

	const handleSubmit = async () => {
		setIsSubmitting(true);
		if (props.userDetails.firstName.trim() && props.userDetails.email && tscsSelected) {
			const submitted = await props.onSubmit(props.userDetails, tscsSelected, optinSelected);

			setShowSuccessMessage(submitted);

			if (submitted) {
				setTimeout(() => {
					closeModal();
				}, 4000);
			}
		}
		setIsSubmitting(false);
	}

	// the submit button is enabled only once the terms are accepted and a valid email and first name have been entered.
	// the first name is required because the generated PDF greets the customer by it - without one the greeting reads 'Dear ,'.
	// it is trimmed, as a name of only spaces would pass the input's own required check and leave the same empty greeting.
	const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(props.userDetails.email);
	const isFirstNameValid = props.userDetails.firstName.trim().length > 0;
	const isDisabled = isSubmitting || !tscsSelected || !isEmailValid || !isFirstNameValid;

	// return the HTML.
	return (
		<React.Fragment>
			{props.trigger && props.trigger(openModal)}
			<div onClick={(e: React.MouseEvent) => e.stopPropagation()} id="modal-container" className={isOpen ? 'open' : ''}>
				<div id="modal-body" className="min-height pdf-modal">
					<div className="close" role="button" onClick={closeModal}>
						<img src="assets/images/icons/close.svg" width="35" height="35" alt="Close" />
					</div>
					<img id="pdf-img" src="assets/images/misc/pdf-booklet.webp" width="100%" height="auto" />
					{showSuccessMessage ? (
						<h4 id="thank-you-pdf" >
							Thank you, a link to download your specification summary will be emailed to you.
						</h4>
					) : (
						<React.Fragment>
							<h3>YOUR CONFIGURATION WILL BE EMAILED TO YOU</h3>
							<div className="pdf-input-container">
								<FormInput
									handleUpdateInput={(id, val) => props.updateUserDetails('firstName', val)}
									inputValue={props.userDetails.firstName}
									input={{
										id: 'firstName',
										placeholder: 'First Name',
										required: true,
									}}
								/>
								<FormInput
									handleUpdateInput={(id, val) => props.updateUserDetails('lastName', val)}
									inputValue={props.userDetails.lastName}
									input={{
										id: 'lastName',
										placeholder: 'Last Name',
									}}
								/>
								<FormInput
									handleUpdateInput={(id, val) => props.updateUserDetails('email', val)}
									inputValue={props.userDetails.email}
									input={{
										id: 'email',
										placeholder: 'Email',
										required: true,
									}}
								/>
							</div>
							<div className="optin-container">
								<p>We do not supply your details to any third parties unless you specifically ask us to.</p>
								<div className="checkbox-container">
									<div style={{ height: 'fit-content', width: 'fit-content', borderRadius: 20 }}>
										<input type="checkbox" id="tscs" name="tscs" value="tscs" onChange={() => setTscsSelected(!tscsSelected)} />
									</div>
									<label htmlFor="tscs">By submitting this form I agree to the Terms & Conditions and Privacy Policy</label>
								</div>
								<div className="checkbox-container">
									<input type="checkbox" id="optin" checked={optinSelected} name="optin" value="optin" onChange={() => setOptinSelected(!optinSelected)} />
									<label htmlFor="optin">I wish to Opt-in to communication from Morgan and to be kept in the customer database.</label>
								</div>
							</div>
							<button id="submit-pdf" onClick={handleSubmit} disabled={isDisabled}>SUBMIT</button>
						</React.Fragment>
					)}
				</div>
			</div>
		</React.Fragment>
	);
}
